import React from 'react';
import { Box, Typography, Chip, Divider } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { COLORS } from '../styles';
import { getFriendlyName, STAGE_LABELS } from '../utils';

// Mock previous runs for the demo. Drives the clickable history list.
export const PREVIOUS_RUNS = [
  { id: 'r1', flightNumber: 'WN 1139', from: 'MCO', stops: 6, bags: 38, ago: '32m ago',   startedAt: '05:42', endedAt: '06:14', onTime: true,  durationMin: 32 },
  { id: 'r2', flightNumber: 'WN 4457', from: 'SEA', stops: 4, bags: 22, ago: '1h 18m ago', startedAt: '04:55', endedAt: '05:25', onTime: true,  durationMin: 30 },
  { id: 'r3', flightNumber: 'WN 803',  from: 'PHX', stops: 3, bags: 15, ago: '2h 5m ago',  startedAt: '04:08', endedAt: '04:32', onTime: false, durationMin: 24 },
];

function HomeScreen({
  driver, inboundETA, inboundGate, otherDriverCount = 0,
  hasAssignment, runStage, isLate,
  onOpenAssignment, onResumeRun, onOpenPreviousRun,
}) {
  const friendlyName = getFriendlyName(driver?.id);
  const flights = driver?.flights || [];
  const stats = { bagsToday: 142, stopsToday: 18, runsToday: 3 };
  const totalBags = flights.reduce((t, f) => t + (f.bags || 0), 0);
  const splitType = getDriverSplitType(driver, flights);

  const showResume = !!runStage;
  const stageLabel = runStage ? STAGE_LABELS[runStage] || 'In run' : '';

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      {/* ─── SECTION 1: Current Status ───────────────────────────── */}
      <Box sx={{ p: 2, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          {/* Left half — agent identity & status */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Welcome back
            </Typography>
            <Typography sx={{ color: COLORS.textPrimary, fontSize: '1.4rem', fontWeight: 700, lineHeight: 1.1, mt: 0.25 }}>
              {friendlyName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
              <Chip
                icon={<CircleIcon sx={{ fontSize: '0.6rem !important', color: `${COLORS.success} !important` }} />}
                label="ON SHIFT"
                size="small"
                sx={{
                  backgroundColor: 'rgba(34, 197, 94, 0.15)',
                  color: COLORS.success,
                  fontSize: '0.58rem', fontWeight: 700, letterSpacing: 0.5,
                  height: 20,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <ScheduleIcon sx={{ fontSize: 11, color: COLORS.textMuted }} />
                <Typography sx={{ fontSize: '0.65rem', color: COLORS.textMuted, fontWeight: 600 }}>
                  6-14b
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right half — equipment & dispatch contacts */}
          <Box sx={{
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.6,
            pt: 0.25,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <AirportShuttleIcon sx={{ fontSize: 14, color: COLORS.accent }} />
              <Typography sx={{ fontSize: '0.7rem', color: COLORS.accent, fontWeight: 700 }}>
                Tug 28876
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.4 }}>
                <HeadsetMicIcon sx={{ fontSize: 11, color: COLORS.textMuted }} />
                <Typography sx={{ fontSize: '0.55rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Dispatch
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.65rem', color: COLORS.textSecondary, mt: 0.1 }}>
                <span style={{ color: COLORS.textPrimary, fontWeight: 600 }}>Ryan</span>
                <span style={{ color: COLORS.textMuted }}> · S1</span>
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: COLORS.textSecondary }}>
                <span style={{ color: COLORS.textPrimary, fontWeight: 600 }}>Stone</span>
                <span style={{ color: COLORS.textMuted }}> · S2</span>
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Status card */}
        {showResume ? (
          <ResumeCard isLate={isLate} stageLabel={stageLabel} flightCount={flights.length} onClick={onResumeRun} />
        ) : hasAssignment ? (
          <PendingAssignmentCard
            flightCount={flights.length}
            bagCount={totalBags}
            inboundETA={inboundETA}
            inboundGate={inboundGate}
            otherDriverCount={otherDriverCount}
            splitType={splitType}
            onClick={onOpenAssignment}
          />
        ) : (
          <IdleCard />
        )}
      </Box>

      <Divider sx={{ borderColor: COLORS.divider, mx: 2, my: 0.5 }} />

      {/* ─── SECTION 2: Today + Previous Runs ────────────────────── */}
      <Box sx={{ p: 2, pt: 1.5 }}>
        <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.75 }}>
          Today
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <StatTile label="Bags"  value={stats.bagsToday} />
          <StatTile label="Stops" value={stats.stopsToday} />
          <StatTile label="Runs"  value={stats.runsToday} />
        </Box>

        <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.6, mt: 2, mb: 0.5 }}>
          Previous Runs
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {PREVIOUS_RUNS.map((run) => (
            <Box
              key={run.id}
              onClick={() => onOpenPreviousRun?.(run)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 0.85,
                cursor: 'pointer',
                borderBottom: `1px solid ${COLORS.divider}`,
                '&:last-child': { borderBottom: 'none' },
                '&:active': { backgroundColor: COLORS.surface },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: COLORS.textPrimary, fontSize: '0.78rem', fontWeight: 600 }}>
                  {run.flightNumber} <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>· from {run.from}</span>
                </Typography>
                <Typography sx={{ color: COLORS.textMuted, fontSize: '0.6rem' }}>
                  {run.stops} stops · {run.bags} bags · {run.ago}
                </Typography>
              </Box>
              <Typography sx={{
                color: run.onTime ? COLORS.success : COLORS.warn,
                fontSize: '0.55rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                mr: 0.5,
              }}>
                {run.onTime ? 'ON TIME' : 'LATE'}
              </Typography>
              <ChevronRightIcon sx={{ color: COLORS.textMuted, fontSize: 16 }} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// If every flight on the run has the same splitType, we treat the whole
// run as that compartment (FWD or AFT). Mixed/unsplit returns null.
function getDriverSplitType(driver, flights) {
  // Sometimes the split shows up in the driver name itself (e.g. "Driver 2 FWD")
  if (driver?.name) {
    if (/\bFWD\b/i.test(driver.name)) return 'FWD';
    if (/\bAFT\b/i.test(driver.name)) return 'AFT';
  }
  if (!flights?.length) return null;
  const types = new Set(flights.map(f => f.splitType).filter(Boolean));
  if (types.size === 1) return [...types][0];
  return null;
}

function ResumeCard({ isLate, stageLabel, flightCount, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        mt: 1.5,
        backgroundColor: isLate ? 'rgba(244, 63, 94, 0.18)' : COLORS.surface,
        borderRadius: 2,
        p: 1.5,
        cursor: 'pointer',
        border: `1px solid ${isLate ? COLORS.danger : COLORS.accent}`,
        display: 'flex', alignItems: 'center', gap: 1.25,
        '&:active': { opacity: 0.85 },
      }}
    >
      {isLate ? (
        <WarningAmberIcon sx={{ color: COLORS.danger, fontSize: 28 }} />
      ) : (
        <PlayArrowIcon sx={{ color: COLORS.accent, fontSize: 28 }} />
      )}
      <Box sx={{ flex: 1 }}>
        <Typography sx={{
          fontSize: '0.55rem',
          color: isLate ? COLORS.danger : COLORS.accent,
          fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6,
        }}>
          {isLate ? 'Running Late' : 'In Progress'}
        </Typography>
        <Typography sx={{ fontSize: '0.82rem', color: COLORS.textPrimary, fontWeight: 700, lineHeight: 1.1 }}>
          {stageLabel}
        </Typography>
        <Typography sx={{ fontSize: '0.6rem', color: COLORS.textMuted, mt: 0.25 }}>
          {flightCount} stops · Tap to resume ▸
        </Typography>
      </Box>
    </Box>
  );
}

function PendingAssignmentCard({ flightCount, bagCount, inboundETA, inboundGate, otherDriverCount, splitType, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        mt: 1.5,
        background: `linear-gradient(135deg, ${COLORS.accentDim} 0%, ${COLORS.accent} 100%)`,
        borderRadius: 2,
        p: 1.5,
        cursor: 'pointer',
        animation: 'pulseGlow 1.6s ease-in-out infinite',
        '@keyframes pulseGlow': {
          '0%, 100%': { boxShadow: `0 0 14px rgba(34, 211, 238, 0.35)` },
          '50%': { boxShadow: `0 0 28px rgba(34, 211, 238, 0.7)` },
        },
        '&:active': { transform: 'scale(0.98)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <NotificationsActiveIcon sx={{ color: '#0f172a', fontSize: 16 }} />
          <Typography sx={{ color: '#0f172a', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7 }}>
            New Assignment
          </Typography>
        </Box>
        {splitType && (
          <Box sx={{
            backgroundColor: '#0f172a',
            color: COLORS.accent,
            px: 0.85, py: 0.15,
            borderRadius: 0.75,
            fontSize: '0.55rem',
            fontWeight: 800,
            letterSpacing: 0.6,
          }}>
            {splitType}
          </Box>
        )}
      </Box>

      {/* 2x2 mini-grid of key info */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 0.75,
        p: 1,
      }}>
        <InfoCell label="ETA" value={inboundETA || '—'} />
        <InfoCell label="Gate" value={inboundGate || '—'} />
        <InfoCell label="Stops" value={flightCount} />
        <InfoCell label="Bags" value={bagCount} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.85 }}>
        <Typography sx={{ color: 'rgba(15,23,42,0.85)', fontSize: '0.62rem', fontWeight: 600 }}>
          {otherDriverCount === 0
            ? 'Solo run'
            : `+ ${otherDriverCount} other driver${otherDriverCount === 1 ? '' : 's'}`}
        </Typography>
        <Typography sx={{ color: '#0f172a', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase' }}>
          Tap to acknowledge ▸
        </Typography>
      </Box>
    </Box>
  );
}

function InfoCell({ label, value }) {
  return (
    <Box>
      <Typography sx={{ color: 'rgba(15,23,42,0.55)', fontSize: '0.5rem', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography sx={{ color: '#0f172a', fontSize: '0.85rem', fontWeight: 700, lineHeight: 1 }}>
        {value}
      </Typography>
    </Box>
  );
}

function IdleCard() {
  return (
    <Box sx={{
      mt: 1.5,
      backgroundColor: COLORS.surface,
      borderRadius: 2,
      p: 1.5,
      border: `1px dashed ${COLORS.divider}`,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', textAlign: 'center',
    }}>
      <HourglassEmptyIcon sx={{ color: COLORS.textMuted, fontSize: 24, mb: 0.5 }} />
      <Typography sx={{ color: COLORS.textPrimary, fontSize: '0.78rem', fontWeight: 600 }}>
        Waiting on dispatch
      </Typography>
      <Typography sx={{ color: COLORS.textMuted, fontSize: '0.65rem', mt: 0.25 }}>
        Your next run will arrive here.
      </Typography>
    </Box>
  );
}

function StatTile({ label, value }) {
  return (
    <Box sx={{ flex: 1, backgroundColor: COLORS.surface, borderRadius: 1.5, p: 0.85, textAlign: 'center' }}>
      <Typography sx={{ color: COLORS.accent, fontSize: '1rem', fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: COLORS.textMuted, fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.25 }}>{label}</Typography>
    </Box>
  );
}

export default HomeScreen;
